'use client';
import React from 'react';

import ListPage from '../../../../../components/page-components/list-page/list-page';
import { createListCardArray } from '../../../../../helpers/list-page/list-page-helpers';
import { pageIsEvent } from '../../../../../helpers/list-page/type-guards';
import useGetMobileAppWebViewHeader from '../../../../../hooks/use-get-mobile-app-web-view';
import { IMeetingplacePageHomeMinifiedExtendedLogo } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageOccations } from '@/lib/fragments/meetingPlacePageOccations';
import { IMeetingPlacePageEventList } from '@/lib/fragments/meetingPlacePageEventList';
import { useTenantConfig } from '@/providers/ConfigProvider';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';

interface MeetingPlacePageEventListProps {
  metaData: any;
  node_locale: string;
  urlSubdirectory?: string;
  occasionId?: string | null;
  eventListData: {
    meetingPlacePageEventList: IMeetingPlacePageEventList | null;
    meetingPlacePageOccasions: IMeetingPlacePageOccations | null;
    meetingPlacePageHomeCollection: IMeetingplacePageHomeMinifiedExtendedLogo | null;
  } | null;
  initialPage?: number;
  slotsData?: any;
  ikeaFamilyBenefit?:
    | import('@/lib/getIkeaFamilyBenefits').IkeaFamilyBenefitItem
    | null;
}

const MeetingPlacePageEventList = ({
  metaData,
  node_locale,
  urlSubdirectory,
  occasionId,
  eventListData,
  initialPage,
  slotsData,
  ikeaFamilyBenefit,
}: MeetingPlacePageEventListProps) => {
  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX || '';
  const isWebView = useGetMobileAppWebViewHeader();

  if (!eventListData) {
    return null;
  }

  const page = eventListData.meetingPlacePageEventList;
  const homepage = eventListData.meetingPlacePageHomeCollection;
  const timezone = homepage?.meetingPlace?.timezone || '';

  const eventPagesInOccasionList =
    eventListData.meetingPlacePageOccasions &&
    eventListData.meetingPlacePageOccasions?.occasionsCollection?.items
      ?.filter(Boolean)
      ?.filter((t: any) => pageIsEvent(t) && t?.sys?.id);

  const eventPages = eventPagesInOccasionList
    ? [...eventPagesInOccasionList]
    : [];
  page?.eventItemsCollection?.items?.filter(Boolean)?.forEach((page: any) => {
    const pageExists =
      eventPages.filter((item) => item?.sys?.id === page?.sys?.id).length > 0;
    if (!pageExists) eventPages.push(page);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventPagesWithSlots = eventPages.map((eventPage: any) => {
    const contentfulId = eventPage?.sys?.id;
    const slotsForEvent = slotsData?.[contentfulId];

    if (!slotsForEvent) return eventPage;

    const futureSlots = slotsForEvent.filter((slotDay: any) => {
      const slotDate = new Date(slotDay.date);
      slotDate.setHours(0, 0, 0, 0);
      return slotDate >= today;
    });

    return {
      ...eventPage,
      slots: futureSlots,
    };
  });

  const eventCardList = createListCardArray(
    eventPagesWithSlots,
    undefined,
    timezone,
    urlSubdirectory,
    node_locale,
    prefix,
  );

  return (
    <>
      {page && (
        <>
          <GtmPageView
            homepage={homepage}
            language={node_locale}
            pageTitle={page?.name ?? ''}
          />

          <ListPage
            cardList={eventCardList}
            pageName={page?.name}
            pageSlug={page?.slug}
            node_locale={node_locale}
            breadcrumbs={[page?.name]}
            config={config}
            homeText={homepage?.breadcrumbHomeText || ''}
            tileColClass="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            listType="event"
            isWebView={isWebView}
            initialPage={initialPage ?? 1}
            ikeaFamilyBenefit={ikeaFamilyBenefit}
          />
        </>
      )}
    </>
  );
};

export default MeetingPlacePageEventList;

